import React, { useCallback, useState } from 'react';
import cx from 'classnames';

import { withAppBasePath } from '@xarvio/frontend-core-platform/lib/basePath';

import styles from '../PageLayoutAuth.module.scss';
import {
  hasLoadedAuthBackground,
  markAuthBackgroundLoaded,
} from '../authBackgroundLoadState';
import { getAuthBackgroundCountry } from './authBackground';

export interface Props {
  className?: string;
  country?: string;
}

export const PageLayoutAuthBg: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  country,
}) => {
  const localCountry = getAuthBackgroundCountry(country);

  const classNames = cx(styles.layoutBg, className, {
    [styles[`layoutBg_country-${localCountry}`]]: localCountry,
  });

  const [loaded, setLoaded] = useState(() =>
    hasLoadedAuthBackground(localCountry),
  );

  const onLoad = useCallback(() => {
    markAuthBackgroundLoaded(localCountry);

    window.requestAnimationFrame(() => setLoaded(true));
  }, [localCountry]);

  // Handle cached images where onLoad may have already fired
  const imgRefCallback = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete && node.naturalWidth > 0) {
        onLoad();
      }
    },
    [onLoad],
  );

  return (
    <div className={classNames}>
      <picture>
        <source
          type="image/avif"
          srcSet={withAppBasePath(`/assets/auth/bg/${localCountry}.avif`)}
        />
        <source
          type="image/webp"
          srcSet={withAppBasePath(`/assets/auth/bg/${localCountry}.webp`)}
        />
        <img
          ref={imgRefCallback}
          className={loaded ? styles.layoutBgImgLoaded : undefined}
          src={withAppBasePath(`/assets/auth/bg/${localCountry}.jpg`)}
          alt=""
          // LCP element: fetchPriority and intrinsic dimensions reserve layout before load.
          fetchPriority="high"
          width={1920}
          height={1080}
          onLoad={onLoad}
        />
      </picture>
    </div>
  );
};
